import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Sparkles, Loader2, ChevronDown, ChevronRight, RefreshCw, User } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { useModels } from '../hooks/ModelsContext';
import { TagEditor } from './TagEditor';
import { SpectrumSlider } from './SpectrumSlider';

const DEFAULT_PERSONA = {
  name: '', age: 35, gender: '', region: '', education: '', employment: '',
  family_status: '', personality_traits: [], background_story: '',
  values: [], political_leaning: '', worldview: '',
  communication_style: '', argumentation_style: '', openness_to_change: 5,
  life_experiences: [], pain_points: [], aspirations: [],
  income_bracket: '', urban_rural: '', digital_literacy: '',
  topic_attitudes: {}, relevant_experiences: [],
  archetype: '', default_model: '', tags: [], notes: '',
};

export function PersonaEditor({ library }) {
  const { models: MODELS } = useModels();
  const { t } = useTranslation();
  const [persona, setPersona] = useState({ ...DEFAULT_PERSONA });
  const [expandedSections, setExpandedSections] = useState({ identity: true, demographics: true });
  const [isSaving, setIsSaving] = useState(false);
  const [topicKey, setTopicKey] = useState('');
  const [topicValue, setTopicValue] = useState('');

  const isNew = !library.editingPersona?.id;

  useEffect(() => {
    if (library.editingPersona) {
      setPersona({ ...DEFAULT_PERSONA, ...library.editingPersona });
    } else {
      setPersona({ ...DEFAULT_PERSONA });
    }
  }, [library.editingPersona]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const update = (field, value) => {
    setPersona(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isNew) {
        await library.createPersona(persona);
      } else {
        await library.updatePersona(persona.id, persona);
      }
      library.closeEditor();
    } catch (e) { console.error(e); }
    finally { setIsSaving(false); }
  };

  const handleEnrich = async () => {
    if (!persona.id) return;
    const enriched = await library.enrichPersona(persona.id);
    if (enriched) setPersona({ ...DEFAULT_PERSONA, ...enriched });
  };

  const handleRegenerateStory = async () => {
    if (!persona.id) return;
    const updated = await library.regenerateStory(persona.id);
    if (updated) setPersona(prev => ({ ...prev, background_story: updated.background_story }));
  };

  const addTopicAttitude = () => {
    if (topicKey.trim() && topicValue.trim()) {
      update('topic_attitudes', { ...persona.topic_attitudes, [topicKey.trim()]: topicValue.trim() });
      setTopicKey('');
      setTopicValue('');
    }
  };

  const removeTopicAttitude = (key) => {
    const { [key]: _, ...rest } = persona.topic_attitudes;
    update('topic_attitudes', rest);
  };

  const SectionHeader = ({ sectionKey, title, icon }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      className="w-full flex items-center justify-between py-3 text-left"
    >
      <h3 className="font-serif text-lg font-medium flex items-center gap-2">
        <span>{icon}</span> {title}
      </h3>
      {expandedSections[sectionKey] ? <ChevronDown className="w-5 h-5 text-stone-400" /> : <ChevronRight className="w-5 h-5 text-stone-400" />}
    </button>
  );

  const AiButton = ({ onClick, label }) => (
    <button
      onClick={onClick}
      disabled={library.isAiWorking || isNew}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
    >
      {library.isAiWorking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {label || t('personaLib.aiGenerate')}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={library.closeEditor} className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
          <ArrowLeft className="w-5 h-5" />
          {t('personaLib.backToLibrary')}
        </button>
        <div className="flex items-center gap-3">
          {!isNew && (
            <button onClick={handleEnrich} disabled={library.isAiWorking} className="btn btn-secondary flex items-center gap-2">
              {library.isAiWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {t('personaLib.enrichAll')}
            </button>
          )}
          <button onClick={handleSave} disabled={isSaving || !persona.name} className="btn btn-primary flex items-center gap-2">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? t('personaLib.create') : t('personaLib.save')}
          </button>
        </div>
      </div>

      {/* Identity Section */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="identity" title={t('personaLib.sectionIdentity')} icon="👤" />
        {expandedSections.identity && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.namePlaceholder')} *</label>
                <input type="text" value={persona.name} onChange={e => update('name', e.target.value)} className="input w-full text-lg" placeholder={t('personaLib.namePlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.archetype')}</label>
                <input type="text" value={persona.archetype} onChange={e => update('archetype', e.target.value)} className="input w-full" placeholder={t('personaLib.archetypePlaceholder')} />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.ageLabel')}</label>
                <input type="number" value={persona.age} onChange={e => update('age', parseInt(e.target.value) || 0)} className="input w-full" min={18} max={100} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.genderLabel')}</label>
                <select value={persona.gender} onChange={e => update('gender', e.target.value)} className="input w-full">
                  <option value="">--</option>
                  <option value="Male">{t('persona.genderMale')}</option>
                  <option value="Female">{t('persona.genderFemale')}</option>
                  <option value="Other">{t('persona.genderOther')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.regionLabel')}</label>
                <input type="text" value={persona.region} onChange={e => update('region', e.target.value)} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('report.colModel')}</label>
                <select value={persona.default_model} onChange={e => update('default_model', e.target.value)} className="input w-full">
                  <option value="">--</option>
                  {Object.entries(MODELS).map(([id, m]) => (
                    <option key={id} value={id}>{m.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Demographics Section */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="demographics" title={t('personaLib.sectionDemographics')} icon="📊" />
        {expandedSections.demographics && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.educationLabel')}</label>
              <input type="text" value={persona.education} onChange={e => update('education', e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.employmentLabel')}</label>
              <input type="text" value={persona.employment} onChange={e => update('employment', e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.familyStatus')}</label>
              <input type="text" value={persona.family_status} onChange={e => update('family_status', e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.urbanRural')}</label>
              <select value={persona.urban_rural} onChange={e => update('urban_rural', e.target.value)} className="input w-full">
                <option value="">--</option>
                <option value="urban">{t('personaLib.urban')}</option>
                <option value="suburban">{t('personaLib.suburban')}</option>
                <option value="small-town">{t('personaLib.smallTown')}</option>
                <option value="rural">{t('personaLib.rural')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.incomeBracket')}</label>
              <select value={persona.income_bracket} onChange={e => update('income_bracket', e.target.value)} className="input w-full">
                <option value="">--</option>
                <option value="low">{t('personaLib.incomeLow')}</option>
                <option value="lower-middle">{t('personaLib.incomeLowerMiddle')}</option>
                <option value="middle">{t('personaLib.incomeMiddle')}</option>
                <option value="upper-middle">{t('personaLib.incomeUpperMiddle')}</option>
                <option value="high">{t('personaLib.incomeHigh')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.digitalLiteracy')}</label>
              <select value={persona.digital_literacy} onChange={e => update('digital_literacy', e.target.value)} className="input w-full">
                <option value="">--</option>
                <option value="low">{t('personaLib.dlLow')}</option>
                <option value="medium">{t('personaLib.dlMedium')}</option>
                <option value="high">{t('personaLib.dlHigh')}</option>
                <option value="expert">{t('personaLib.dlExpert')}</option>
              </select>
            </div>
          </div>
        )}
      </section>

      {/* Personality & Communication */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="personality" title={t('personaLib.sectionPersonality')} icon="💬" />
        {expandedSections.personality && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('persona.traitsLabel')}</label>
              <TagEditor tags={persona.personality_traits} onChange={v => update('personality_traits', v)} placeholder={t('personaLib.addTrait')} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.communicationStyle')}</label>
                <input type="text" value={persona.communication_style} onChange={e => update('communication_style', e.target.value)} className="input w-full" placeholder={t('personaLib.communicationPlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.argumentationStyle')}</label>
                <input type="text" value={persona.argumentation_style} onChange={e => update('argumentation_style', e.target.value)} className="input w-full" placeholder={t('personaLib.argumentationPlaceholder')} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.opennessToChange')}</label>
              <SpectrumSlider value={persona.openness_to_change} onChange={v => update('openness_to_change', v)} min={1} max={10} labels={[t('personaLib.resistant'), t('personaLib.adaptable')]} />
            </div>
          </div>
        )}
      </section>

      {/* Values & Worldview */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="values" title={t('personaLib.sectionValues')} icon="🧭" />
        {expandedSections.values && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.coreValues')}</label>
              <TagEditor tags={persona.values} onChange={v => update('values', v)} suggestions={['fairness', 'innovation', 'tradition', 'freedom', 'security', 'equality', 'family', 'sustainability']} placeholder={t('personaLib.addValue')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.politicalLeaning')}</label>
              <select value={persona.political_leaning} onChange={e => update('political_leaning', e.target.value)} className="input w-full">
                <option value="">--</option>
                <option value="left">{t('personaLib.polLeft')}</option>
                <option value="center-left">{t('personaLib.polCenterLeft')}</option>
                <option value="center">{t('personaLib.polCenter')}</option>
                <option value="center-right">{t('personaLib.polCenterRight')}</option>
                <option value="right">{t('personaLib.polRight')}</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-stone-700">{t('personaLib.worldview')}</label>
                <AiButton onClick={handleEnrich} label={t('personaLib.aiGenerate')} />
              </div>
              <textarea value={persona.worldview} onChange={e => update('worldview', e.target.value)} className="input w-full" rows={3} placeholder={t('personaLib.worldviewPlaceholder')} />
            </div>
          </div>
        )}
      </section>

      {/* Life Story */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="life" title={t('personaLib.sectionLife')} icon="📖" />
        {expandedSections.life && (
          <div className="space-y-4 pt-2">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-stone-700">{t('personaLib.backgroundStory')}</label>
                <AiButton onClick={handleRegenerateStory} label={t('personaLib.regenerate')} />
              </div>
              <textarea value={persona.background_story} onChange={e => update('background_story', e.target.value)} className="input w-full" rows={4} placeholder={t('personaLib.storyPlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.lifeExperiences')}</label>
              <TagEditor tags={persona.life_experiences} onChange={v => update('life_experiences', v)} placeholder={t('personaLib.addExperience')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.painPoints')}</label>
              <TagEditor tags={persona.pain_points} onChange={v => update('pain_points', v)} placeholder={t('personaLib.addPainPoint')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.aspirations')}</label>
              <TagEditor tags={persona.aspirations} onChange={v => update('aspirations', v)} placeholder={t('personaLib.addAspiration')} />
            </div>
          </div>
        )}
      </section>

      {/* Topic-Specific */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="topics" title={t('personaLib.sectionTopics')} icon="🎯" />
        {expandedSections.topics && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">{t('personaLib.topicAttitudes')}</label>
              {Object.entries(persona.topic_attitudes || {}).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-sm text-stone-700 min-w-[100px]">{key}:</span>
                  <span className="text-sm text-stone-600 flex-1">{val}</span>
                  <button onClick={() => removeTopicAttitude(key)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input type="text" value={topicKey} onChange={e => setTopicKey(e.target.value)} className="input flex-1" placeholder={t('personaLib.topicName')} />
                <input type="text" value={topicValue} onChange={e => setTopicValue(e.target.value)} className="input flex-1" placeholder={t('personaLib.topicAttitude')} />
                <button onClick={addTopicAttitude} className="btn btn-secondary text-sm px-3">{t('personaLib.add')}</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.relevantExperiences')}</label>
              <TagEditor tags={persona.relevant_experiences} onChange={v => update('relevant_experiences', v)} placeholder={t('personaLib.addRelevantExperience')} />
            </div>
          </div>
        )}
      </section>

      {/* Meta */}
      <section className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <SectionHeader sectionKey="meta" title={t('personaLib.sectionMeta')} icon="🏷️" />
        {expandedSections.meta && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.tags')}</label>
              <TagEditor tags={persona.tags} onChange={v => update('tags', v)} placeholder={t('personaLib.addTag')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('personaLib.notes')}</label>
              <textarea value={persona.notes} onChange={e => update('notes', e.target.value)} className="input w-full" rows={3} placeholder={t('personaLib.notesPlaceholder')} />
            </div>
            {persona.version && (
              <div className="text-sm text-stone-400">
                {t('personaLib.version')}: {persona.version} | {t('personaLib.usedTimes', { count: persona.usage_count || 0 })}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
